import { isEmpty } from "lodash";
import { createContext, useMemo, useReducer, useState } from "react";

import {
  registerFieldAction,
  resetErrorsAction,
  resetFormAction,
  setFieldErrorsAction,
  setFieldValueAction,
  setFormErrorsAction,
  setFormLoadingAction
} from "seneca-common/utils/form/state/actions";
import formReducer, { initState } from "seneca-common/utils/form/state/reducer";
import {
  ContextState,
  Errors,
  Form,
  FormProviderProps,
  RegisterField,
  Validator
} from "seneca-common/utils/form/types";
import { getSafe } from "seneca-common/utils/form/utils";

const FormContext = createContext<ContextState | undefined>(undefined);

function FormProvider({ children, onSubmitSuccess }: FormProviderProps) {
  const [formState, dispatch] = useReducer(formReducer, initState());
  const [isDirty, setIsDirty] = useState<boolean>(false);

  function useGetForm(form: Form) {
    const getFieldValue = (fieldName: string) => {
      return getSafe(() => formState.data[form][fieldName]) || "";
    };

    const getFirstFieldError = (fieldName: string) => {
      const error = getSafe(() => formState.errors[form][fieldName][0]);
      return error ? { showError: !!error, error: error } : undefined;
    };

    const getFormErrors = () => getSafe(() => formState.errors[form][form], []);

    const getFormLoading = () => formState.loaders[form];

    const registerField = useMemo(
      () =>
        ({ fieldName, defaultValue, validators }: RegisterField) => {
          dispatch(
            registerFieldAction({
              form,
              fieldName,
              defaultValue,
              validators
            })
          );
        },
      [form]
    );

    const setFieldValue = (fieldName: string, value: any) => {
      if (!isDirty) {
        setIsDirty(true);
      }
      dispatch(setFieldValueAction({ form, fieldName, value }));
    };

    const setFormErrors = (errors: string[]) => {
      dispatch(setFormErrorsAction({ form, errors }));
    };

    const setFormLoading = (isLoading: boolean) => {
      dispatch(setFormLoadingAction({ form, isLoading }));
    };

    const submitForm = () => {
      if (validateForm(form)) {
        onSubmitSuccess({
          data: formState.data,
          form,
          setFormLoading,
          setFormErrors,
          validateForm
        });
      }
    };

    const resetForm = () => {
      dispatch(resetFormAction());
    };

    const validateField = (fieldName: string) => {
      const { validators, data } = formState;
      const value = getFieldValue(fieldName);

      if (!formState.data[form]) return false;
      if (isEmpty(validators[form])) return true;

      const fieldValidators = validators[form][fieldName];

      const messages = fieldValidators.reduce(
        (result: string[], validator: Validator) => {
          const err = validator(value, data);
          return [...result, ...err];
        },
        []
      );

      if (messages.length === 0) return true;

      const formFieldsErrors = {
        [form]: {
          [fieldName]: messages
        }
      };

      dispatch(
        setFieldErrorsAction({
          form,
          formFieldsErrors
        })
      );

      return false;
    };

    const validateForm = (_form: string) => {
      const { validators } = formState;

      dispatch(resetErrorsAction({ form: _form }));

      if (!formState.data[_form]) return false;
      if (isEmpty(validators[_form])) return true;

      const formFieldsErrors = Object.entries(validators[_form]).reduce(
        (
          errors: { [key: string]: Errors },
          [fieldName, validators]: [string, Validator[]]
        ) => {
          const { data } = formState;
          const messages = validators.reduce(
            (result: string[], validator: Validator) => {
              const value = data[_form][fieldName];
              const err = validator(value, data);
              return [...result, ...err];
            },
            []
          );

          if (messages.length > 0) {
            errors[_form] = { ...errors[_form], [fieldName]: messages };
          }

          return errors;
        },
        {}
      );

      if (isEmpty(formFieldsErrors)) return true;

      dispatch(setFieldErrorsAction({ form: _form, formFieldsErrors }));

      return false;
    };

    return {
      getFieldValue,
      getFirstFieldError,
      getFormErrors,
      getFormLoading,

      registerField,

      setFieldValue,
      setFormErrors,
      setFormLoading,

      validateField,

      submitForm,
      resetForm,
      isDirty
    };
  }

  return (
    <FormContext.Provider value={{ useGetForm }}>
      {children}
    </FormContext.Provider>
  );
}

export { FormContext, FormProvider };
